import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

export type SelectableStatsPanelItem<TId extends string | number> = {
  id: TId;
  label: string;
  count: number;
  color: string;
  tooltipLabel: string;
};

type SelectableStatsPanelProps<TId extends string | number> = {
  items: SelectableStatsPanelItem<TId>[];
  totalNodes: number;
  selectedIds: Set<TId>;
  onToggle: (id: TId) => void;
  onClear: () => void;
  footerText?: string;
  maxHeight?: number;
};

export const SelectableStatsPanel = <TId extends string | number>({
  items,
  totalNodes,
  selectedIds,
  onToggle,
  onClear,
  footerText,
  maxHeight,
}: SelectableStatsPanelProps<TId>) => {
  const isFiltering = selectedIds.size > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Chip
          label="Clear"
          size="small"
          variant="outlined"
          onClick={onClear}
          color={isFiltering ? 'default' : 'primary'}
        />
      </Box>

      <Stack spacing={0.5} sx={maxHeight ? { maxHeight, overflow: 'auto' } : undefined}>
        {items.map((item) => {
          const isActive = !isFiltering || selectedIds.has(item.id);
          const percentage = totalNodes > 0 ? (item.count / totalNodes) * 100 : 0;
          const pctLabel = `${Math.round(percentage)}%`;

          return (
            <Box
              key={String(item.id)}
              onClick={() => onToggle(item.id)}
              sx={{
                cursor: 'pointer',
                p: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
                opacity: isActive ? 1 : 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={item.label}
                >
                  {item.label}
                </Typography>
                <Tooltip
                  title={`Percentage of nodes in the current graph view belonging to this ${item.tooltipLabel}: ${item.count} of ${totalNodes}`}
                  arrow
                >
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {item.count} ({pctLabel})
                  </Typography>
                </Tooltip>
              </Box>

              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': { bgcolor: item.color },
                }}
              />
            </Box>
          );
        })}
      </Stack>

      {footerText ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {footerText}
        </Typography>
      ) : null}
    </Box>
  );
};
